import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import { AuthContext } from "../../context/AuthContext";
import KitchenDetails from "./KitchenDetails";
import KitchenLocation from "./KitchenLocation";
import KitchenPrice from "./KitchenPrice";
import KitchenImages from "./KitchenImages";
import KitchenDocuments from "./KitchenDocuments";
import KitchenAmenities from "./KitchenAmenities";
import { AMENITY_CATEGORIES } from "./KitchenAmenities";
import KitchenOwnerTermsModal from "../modals/modalVariants/KitchenOwnerTermsModal";
import AuthModal from "../modals/AuthModal/AuthModal";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import smoothscroll from "smoothscroll-polyfill";
import "./AddKitchen.css";
import axios from "axios";
import config from "../../config";

const AddKitchen = ({ editMode }) => {
  const { kitchenID } = useParams();
  const { login } = React.useContext(AuthContext);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState("login");
  const pendingSubmission = useRef(null);

  // Centralized state for all kitchen fields
  const [kitchenData, setKitchenData] = useState({
    title: "",
    category: "",
    size: "",
    city: "",
    neighborhood: "",
    description: "",
    checkinmessage: "",
    agreed: false,
    bookingType: "hour",
    hourlyRate: "",
    dayRate: "",
    cleaningFee: "",
    minBooking: "",
    securityDeposit: "",
    rateEntries: [],
    images: [],
    address: "",
    latitude: null,
    longitude: null,
    amenities: {},
    documents: [],
    timeZone: "",
  });

  // State for collapsible panels
  const allPanels = [
    "details",
    "location",
    "price",
    "images",
    "documents",
    "amenities",
  ];
  const [activePanels, setActivePanels] = useState(allPanels);

  // Modal and message states
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [errors, setErrors] = useState({});
  const [successMsg, setSuccessMsg] = useState("");

  const showToast = (message, type = "success") => {
    if (type === "success") {
      toast.success(message, { icon: "✅" });
    } else if (type === "error") {
      toast.error(message);
    } else if (type === "info") {
      toast.info(message);
    }
  };

  const openTermsModal = () => setShowTermsModal(true);
  const closeTermsModal = () => setShowTermsModal(false);
  const openAuthModal = (mode = "login") => {
    setAuthMode(mode);
    setShowAuthModal(true);
  };
  const closeAuthModal = () => {
    setShowAuthModal(false);
    if (pendingSubmission.current && localStorage.getItem("token")) {
      handleSubmit(pendingSubmission.current);
      pendingSubmission.current = null;
    }
  };

  const userIDSet = useRef(false);

  const updateKitchenData = useCallback((newData) => {
    setKitchenData((prev) => ({ ...prev, ...newData }));
    setErrors({});
  }, []);

  useEffect(() => {
    if (!userIDSet.current) {
      const userID = localStorage.getItem("userID");
      if (userID && !kitchenData.UserID) {
        updateKitchenData({ UserID: userID });
        userIDSet.current = true;
      }
    }
  }, [updateKitchenData, kitchenData.UserID]);

  // Fetch kitchen data if in edit mode
  useEffect(() => {
    if (editMode && kitchenID) {
      const fetchKitchenData = async () => {
        try {
          const response = await axios.get(
            `${config.apiBaseUrl}/api/kitchens/kitchen/${kitchenID}`,
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("token")}`,
              },
            }
          );
          const fetchedData = {
            KitchenID: response.data.KitchenID,
            title: response.data.Title,
            category: response.data.Category,
            size: response.data.Size,
            city: response.data.City,
            neighborhood: response.data.Neighborhood,
            description: response.data.Description,
            checkinmessage: response.data.CheckinMessage,
            agreed: response.data.Agreed,
            bookingType: response.data.BookingType,
            hourlyRate: response.data.HourlyRate,
            dayRate: response.data.DayRate,
            cleaningFee: response.data.CleaningFee,
            minBooking: response.data.MinBooking,
            securityDeposit: response.data.SecurityDeposit,
            rateEntries: response.data.RateEntries || [],
            images: response.data.Images || [],
            address: response.data.Address,
            latitude: response.data.Latitude,
            longitude: response.data.Longitude,
            amenities: response.data.Amenities || {},
            documents: response.data.Documents || [],
            timeZone: response.data.timeZone || "",
          };
          setKitchenData(fetchedData);
        } catch (error) {
          console.error("Error fetching kitchen data:", error);
        }
      };
      fetchKitchenData();
    }
  }, [editMode, kitchenID]);

  useEffect(() => {
    smoothscroll.polyfill();
  }, []);

  // Toggle panels open/close
  const handlePanelToggle = (panelName) => {
    setActivePanels((prev) =>
      prev.includes(panelName)
        ? prev.filter((p) => p !== panelName)
        : [...prev, panelName]
    );
  };

  // Updated submission handler with inline error handling
  const handleSubmit = async (e) => {
    e.preventDefault();

    const token = localStorage.getItem("token");
    if (!token) {
      pendingSubmission.current = e;
      openAuthModal("login");
      return;
    }

    let newErrors = {};

    if (!kitchenData.title || kitchenData.title.trim() === "") {
      newErrors.title = "Please provide a kitchen title.";
    }
    if (!kitchenData.agreed) {
      newErrors.agreed = "You must agree to the Terms and Conditions.";
    }
    if (
      !kitchenData.address ||
      kitchenData.address.trim() === "" ||
      !kitchenData.latitude ||
      !kitchenData.longitude
    ) {
      newErrors.location = "Please provide a valid location for the kitchen.";
    }

    if (Object.keys(newErrors).length > 0) {
      Object.values(newErrors).forEach((msg) => showToast(msg, "error"));
      return;
    }

    // Process Images
    let imagesArray = kitchenData.images || [];
    const newImageEntries = imagesArray.filter((entry) => entry && entry.file);
    const existingImageUrls = imagesArray
      .filter((entry) => entry && !entry.file)
      .map((entry) =>
        typeof entry === "string" ? entry : entry.url || entry.preview
      );

    if (newImageEntries.length > 0) {
      const formDataImages = new FormData();
      newImageEntries.forEach((entry) => {
        formDataImages.append("images", entry.file);
      });

      try {
        const responseImages = await axios.post(
          `${config.apiBaseUrl}/api/kitchens/upload-images`,
          formDataImages,
          {
            headers: {
              "Content-Type": "multipart/form-data",
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        const uploadedImageUrls = responseImages.data.imageUrls;
        kitchenData.images = [...existingImageUrls, ...uploadedImageUrls];
      } catch (error) {
        console.error("Error uploading images:", error.message);
        showToast("Failed to upload images. Please try again.", "error");
        return;
      }
    } else {
      kitchenData.images = existingImageUrls;
    }

    // Process Documents:
    const documentsObj = kitchenData.documents || {};
    const formDataDocs = new FormData();

    // For each document type, if a new file is provided, append it using the field name.
    if (
      documentsObj.liabilityInsurance &&
      documentsObj.liabilityInsurance.file
    ) {
      formDataDocs.append(
        "liabilityInsurance",
        documentsObj.liabilityInsurance.file
      );
    }
    if (documentsObj.healthCertificate && documentsObj.healthCertificate.file) {
      formDataDocs.append(
        "healthCertificate",
        documentsObj.healthCertificate.file
      );
    }
    if (documentsObj.businessLicense && documentsObj.businessLicense.file) {
      formDataDocs.append("businessLicense", documentsObj.businessLicense.file);
    }

    if (
      formDataDocs.has("liabilityInsurance") ||
      formDataDocs.has("healthCertificate") ||
      formDataDocs.has("businessLicense")
    ) {
      try {
        const responseDocs = await axios.post(
          `${config.apiBaseUrl}/api/kitchens/upload-docs`,
          formDataDocs,
          {
            headers: {
              "Content-Type": "multipart/form-data",
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        console.log("uploadDocsToS3 response:", responseDocs.data);
        // Merge the returned URLs (in an object) with the existing documents
        const uploadedDocs = responseDocs.data.docs; // e.g., { liabilityInsurance: "url1", healthCertificate: "url2" }
        const updatedDocumentsObj = { ...documentsObj };
        for (const docType in uploadedDocs) {
          updatedDocumentsObj[docType] = {
            ...updatedDocumentsObj[docType],
            file: null, // clear the file
            url: uploadedDocs[docType],
          };
        }
        // Update kitchenData.documents with the updated object
        kitchenData.documents = updatedDocumentsObj;
      } catch (error) {
        console.error("Error uploading documents:", error.message);
        showToast("Failed to upload documents. Please try again.", "error");
        return;
      }
    } else {
      // If no new files were uploaded, keep the existing documents object
      kitchenData.documents = documentsObj;
    }
    // Transform amenities from boolean mapping to the grouped structure
    const transformAmenities = (amenitiesMapping) => {
      const grouped = {};
      Object.entries(AMENITY_CATEGORIES).forEach(([category, amenityList]) => {
        const selected = amenityList.filter(
          (item) => amenitiesMapping[item.name]
        );
        if (selected.length > 0) {
          grouped[category] = selected.map((item) => item.label);
        }
      });
      return grouped;
    };

    if (kitchenData.amenities && typeof kitchenData.amenities === "object") {
      kitchenData.amenities = transformAmenities(kitchenData.amenities);
    }

    try {
      if (editMode) {
        await axios.put(
          `${config.apiBaseUrl}/api/kitchens/edit/${kitchenData.KitchenID}`,
          kitchenData,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
      } else {
        await axios.post(`${config.apiBaseUrl}/api/kitchens/add`, kitchenData, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });
      }
      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
      showToast("Kitchen saved successfully!", "success");
    } catch (error) {
      console.error("Error submitting kitchen data:", error.message);
      showToast("Failed to save kitchen data. Please try again.", "error");
    }
  };

  return (
    <form className="add-kitchen" onSubmit={handleSubmit}>
      <h1>{editMode ? "Edit Kitchen" : "Add Kitchen"}</h1>

      <ToastContainer
        position="top-right"
        pauseOnHover={true}
        autoClose={2000}
        style={{ zIndex: 99999999, position: "fixed", top: "60px" }}
      />

      <div className="collapsible-panels">
        {/* Kitchen Details Panel */}
        <div className="panel">
          <div
            className="panel-header"
            onClick={() => handlePanelToggle("details")}
          >
            <h2>Kitchen Details</h2>
            <span className="arrow-icon">
              {activePanels.includes("details") ? (
                <FaChevronUp />
              ) : (
                <FaChevronDown />
              )}
            </span>
          </div>
          {activePanels.includes("details") && (
            <div className="panel-content">
              <KitchenDetails
                editMode={editMode}
                kitchenData={kitchenData}
                updateKitchenData={updateKitchenData}
                openTermsModal={openTermsModal}
              />
            </div>
          )}
        </div>

        {/* Kitchen Location Panel */}
        <div className="panel">
          <div
            className="panel-header"
            onClick={() => handlePanelToggle("location")}
          >
            <h2>Location</h2>
            <span className="arrow-icon">
              {activePanels.includes("location") ? (
                <FaChevronUp />
              ) : (
                <FaChevronDown />
              )}
            </span>
          </div>
          {activePanels.includes("location") && (
            <div className="panel-content">
              <KitchenLocation
                editMode={editMode}
                kitchenData={kitchenData}
                updateKitchenData={updateKitchenData}
              />
            </div>
          )}
        </div>

        {/* Kitchen Pricing Panel */}
        <div className="panel">
          <div
            className="panel-header"
            onClick={() => handlePanelToggle("price")}
          >
            <h2>Pricing</h2>
            <span className="arrow-icon">
              {activePanels.includes("price") ? (
                <FaChevronUp />
              ) : (
                <FaChevronDown />
              )}
            </span>
          </div>
          {activePanels.includes("price") && (
            <div className="panel-content">
              <KitchenPrice
                editMode={editMode}
                kitchenData={kitchenData}
                updateKitchenData={updateKitchenData}
              />
            </div>
          )}
        </div>

        {/* Kitchen Images Panel */}
        <div className="panel">
          <div
            className="panel-header"
            onClick={() => handlePanelToggle("images")}
          >
            <h2>Images</h2>
            <span className="arrow-icon">
              {activePanels.includes("images") ? (
                <FaChevronUp />
              ) : (
                <FaChevronDown />
              )}
            </span>
          </div>
          {activePanels.includes("images") && (
            <div className="panel-content">
              <KitchenImages
                editMode={editMode}
                kitchenData={kitchenData}
                updateKitchenData={updateKitchenData}
              />
            </div>
          )}
        </div>

        {/* Kitchen Documents Panel */}
        <div className="panel">
          <div
            className="panel-header"
            onClick={() => handlePanelToggle("documents")}
          >
            <h2>Documents</h2>
            <span className="arrow-icon">
              {activePanels.includes("documents") ? (
                <FaChevronUp />
              ) : (
                <FaChevronDown />
              )}
            </span>
          </div>
          {activePanels.includes("documents") && (
            <div className="panel-content">
              <KitchenDocuments
                editMode={editMode}
                kitchenData={kitchenData}
                updateKitchenData={updateKitchenData}
              />
            </div>
          )}
        </div>

        {/* Kitchen Amenities Panel */}
        <div className="panel">
          <div
            className="panel-header"
            onClick={() => handlePanelToggle("amenities")}
          >
            <h2>Amenities</h2>
            <span className="arrow-icon">
              {activePanels.includes("amenities") ? (
                <FaChevronUp />
              ) : (
                <FaChevronDown />
              )}
            </span>
          </div>
          {activePanels.includes("amenities") && (
            <div className="panel-content">
              <KitchenAmenities
                editMode={editMode}
                kitchenData={kitchenData}
                updateKitchenData={updateKitchenData}
              />
            </div>
          )}
        </div>
      </div>
      <div className="form-row">
        <button type="submit" className="submit-kitchen-btn">
          {editMode ? "Update Kitchen" : "Submit for Review"}
        </button>
      </div>

      {/* Terms and Conditions Modal */}
      <KitchenOwnerTermsModal open={showTermsModal} onClose={closeTermsModal} />
      <AuthModal
        open={showAuthModal}
        onClose={closeAuthModal}
        initialMode={authMode}
      />
    </form>
  );
};

export default AddKitchen;
